import {
  ChatBubbleOutlineOutlined as ChatBubbleOutlineIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  Login as LoginIcon,
  MergeTypeOutlined as MergeTypeOutlinedIcon,
  RateReviewOutlined as RateReviewOutlinedIcon,
  SlideshowOutlined as SlideshowOutlinedIcon,
} from "@/components/icons";
import * as React from "react";
import { useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useMe } from "@/api/hooks";
import { LanguageMenuButton } from "@/components/language/LanguageMenuButton";
import { providerList, useAuthProviders } from "@/features/auth/authProviders";
import { brand } from "@/theme";

/** Public landing at ``/``.
 *
 * Signed-out visitors get a short explainer of what Repoworks does
 * and a CTA to ``/login``; signed-in visitors bounce straight to
 * ``/app/sessions``. Before this page existed a forwarded URL dumped
 * a first-time reader into a login wall with zero context for what
 * they were about to sign in to.
 *
 * Kept short and factual on purpose, not a marketing site: the value
 * prop, the four capabilities and the four-step flow are enough for a
 * first-time visitor to decide whether the tool is relevant. The
 * sign-in note names the methods this deployment actually offers.
 */

type Card = {
  title: string;
  description: string;
  icon: React.ReactElement;
};

/** The four capability tiles, named after the real intent slugs so the
 *  copy here matches what the session picker shows. */
function capabilities(t: TFunction): Card[] {
  return [
    {
      title: t("landing.capability.documentation.title"),
      description: t("landing.capability.documentation.body"),
      icon: <DescriptionOutlinedIcon />,
    },
    {
      title: t("landing.capability.presentations.title"),
      description: t("landing.capability.presentations.body"),
      icon: <SlideshowOutlinedIcon />,
    },
    {
      title: t("landing.capability.review.title"),
      description: t("landing.capability.review.body"),
      icon: <RateReviewOutlinedIcon />,
    },
    {
      title: t("landing.capability.pull_requests.title"),
      description: t("landing.capability.pull_requests.body"),
      icon: <MergeTypeOutlinedIcon />,
    },
  ];
}

function steps(t: TFunction): Card[] {
  return [
    {
      title: t("landing.step.project.title"),
      description: t("landing.step.project.body"),
      icon: <MergeTypeOutlinedIcon />,
    },
    {
      title: t("landing.step.ask.title"),
      description: t("landing.step.ask.body"),
      icon: <ChatBubbleOutlineIcon />,
    },
    {
      title: t("landing.step.iterate.title"),
      description: t("landing.step.iterate.body"),
      icon: <RateReviewOutlinedIcon />,
    },
    {
      title: t("landing.step.export.title"),
      description: t("landing.step.export.body"),
      icon: <DescriptionOutlinedIcon />,
    },
  ];
}

export function LandingPage(): React.JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const providers = useAuthProviders();
  const signInMethods = providerList(providers.data ?? []);

  // Authed users land here by accident (muscle-memory URL typing,
  // an old bookmark) — send them inside.
  useEffect(() => {
    if (me.data) navigate("/app/sessions", { replace: true });
  }, [me.data, navigate]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.background }}>
      {/* Hero — same flat background + logo + typography scale as LoginPage,
          so the visual handoff to /login feels continuous. */}
      <Box
        sx={{
          // Flat: no gradients.
          background: brand.page,
          color: "#ffffff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between", gap: 1, mb: 6 }}
          >
            <Stack direction="row" sx={{ alignItems: "center", gap: 1.25, minWidth: 0 }}>
              <Box
                component="img"
                src="/brand/repoworks_logo_white.svg"
                alt="Repoworks"
                sx={{ height: { xs: 28, sm: 32, md: 36 }, width: "auto" }}
              />
            </Stack>
            <Stack direction="row" sx={{ alignItems: "center", gap: 1, flexShrink: 0 }}>
              <LanguageMenuButton />
              {/* One line in every language ("サインイン" wrapped to two);
                  on a phone the icon goes to leave room for a long label
                  ("Kirjaudu sisään"). */}
              <Button
                href="/login"
                variant="contained"
                color="primary"
                endIcon={<LoginIcon />}
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  "& .MuiButton-endIcon": { display: { xs: "none", sm: "inherit" } },
                }}
              >
                {t("auth.sign_in")}
              </Button>
            </Stack>
          </Stack>

          <Stack sx={{ gap: 3, maxWidth: 720, position: "relative", zIndex: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: brand.muted, letterSpacing: 1.6 }}
            >
              Repoworks
            </Typography>
            <Typography
              component="h1"
              variant="h2"
              sx={{
                fontWeight: 600,
                lineHeight: 1.1,
                maxWidth: 640,
                // h2's 3.75rem fits no long compound word on a phone;
                // break one rather than clip it if it still doesn't fit.
                fontSize: { xs: "2.25rem", sm: "3rem", md: "3.75rem" },
                overflowWrap: "break-word",
              }}
            >
              {t("landing.hero_title")}
            </Typography>
            <Box
              sx={{
                width: 72,
                height: 4,
                borderRadius: 999,
                background: brand.muted,
              }}
            />
            <Typography sx={{ opacity: 0.85, maxWidth: 560, fontSize: 18 }}>
              {t("landing.hero_body")}
            </Typography>
            <Stack
              direction="row"
              sx={{ gap: 2, mt: 1, flexWrap: "wrap" }}
            >
              <Button
                href="/login"
                variant="contained"
                size="large"
                color="primary"
                endIcon={<LoginIcon />}
                sx={{ fontWeight: 600 }}
              >
                {t("auth.sign_in")}
              </Button>
              {signInMethods && (
                <Typography
                  variant="body2"
                  sx={{ alignSelf: "center", opacity: 0.7 }}
                >
                  {t("landing.sign_in_methods", { providers: signInMethods })}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Container>
        {/* Decorative watermark — glyph only (matches the login page; the
            filled tile mark reads as a stray panel at this size/opacity). */}
        <Box
          component="img"
          src="/brand/repoworks_glyph_white.svg"
          alt=""
          aria-hidden
          sx={{
            position: "absolute",
            right: { xs: -120, md: -60 },
            bottom: -80,
            width: { xs: 280, md: 420 },
            opacity: 0.12,
            pointerEvents: "none",
          }}
        />
      </Box>

      {/* Capability grid — four tiles (see capabilities()). */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", letterSpacing: 1.6 }}
        >
          {t("landing.capabilities_overline")}
        </Typography>
        <Typography component="h2" variant="h4" sx={{ mt: 0.5, mb: 4, fontWeight: 600 }}>
          {t("landing.capabilities_title")}
        </Typography>
        <Grid container spacing={3}>
          {capabilities(t).map((cap) => (
            <Grid key={cap.title} size={{ xs: 12, sm: 6, md: 3 }}>
              <CapabilityCard {...cap} />
            </Grid>
          ))}
        </Grid>
      </Container>

      <Divider />

      {/* How a session goes — four plain-language steps so a first-
          time reader knows what to expect after they sign in. */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", letterSpacing: 1.6 }}
        >
          {t("landing.steps_overline")}
        </Typography>
        <Typography component="h2" variant="h4" sx={{ mt: 0.5, mb: 4, fontWeight: 600 }}>
          {t("landing.steps_title")}
        </Typography>
        <Grid container spacing={3}>
          {steps(t).map((step, idx) => (
            <Grid key={step.title} size={{ xs: 12, sm: 6, md: 3 }}>
              <StepCard index={idx + 1} {...step} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Footer — intentionally minimal. Link back to appli.se (the
          maker), second login CTA for scrolled-past readers. */}
      <Box sx={{ bgcolor: brand.surface, borderTop: 1, borderColor: "divider" }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              alignItems: { sm: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t("landing.footer", { year: new Date().getFullYear() })}
            </Typography>
            <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
              <Button href="https://appli.se" size="small" color="inherit">
                appli.se
              </Button>
              <Button
                href="/login"
                size="small"
                variant="outlined"
                endIcon={<LoginIcon />}
              >
                {t("auth.sign_in")}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

function CapabilityCard({
  title,
  description,
  icon,
}: Card): React.JSX.Element {
  return (
    <Stack
      sx={{
        height: "100%",
        p: 3,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: brand.surface,
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: "rgba(255, 255, 255, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
        }}
      >
        {icon}
      </Box>
      <Typography component="h3" variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  );
}

function StepCard({
  index,
  title,
  description,
  icon,
}: Card & { index: number }): React.JSX.Element {
  return (
    <Stack
      sx={{
        height: "100%",
        p: 3,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: brand.surface,
        gap: 1.5,
        position: "relative",
      }}
    >
      <Stack direction="row" sx={{ gap: 1.5, alignItems: "center" }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 999,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {index}
        </Box>
        <Box sx={{ color: "text.secondary" }}>{icon}</Box>
      </Stack>
      <Typography component="h3" variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  );
}
